export const DATE_PICKER_ANCHOR_GAP = 4

export type DatePickerAnchorPlacement = 'above' | 'below'

export type DatePickerAnchorRect = {
  top: number
  left: number
  width: number
  placement: DatePickerAnchorPlacement
}

export type DatePickerAnchorOptions = {
  includeTime?: boolean
  monthYearOnly?: boolean
  minWidth?: number
  pickerWidth?: number
  /** Override horizontal placement (e.g. table cells with wide time picker). */
  resolveLeft?: (rect: DOMRect, pickerWidth: number, viewportWidth: number) => number
}

export function estimateDatePickerHeight(options: DatePickerAnchorOptions = {}): number {
  if (options.includeTime) return 400
  if (options.monthYearOnly) return 300
  return 360
}

function defaultResolveLeft(rect: DOMRect, pickerWidth: number, viewportWidth: number): number {
  let left = rect.left
  const maxLeft = viewportWidth - pickerWidth - DATE_PICKER_ANCHOR_GAP
  if (left > maxLeft) left = Math.max(DATE_PICKER_ANCHOR_GAP, maxLeft)
  if (left < DATE_PICKER_ANCHOR_GAP) left = DATE_PICKER_ANCHOR_GAP
  return left
}

export function computeDatePickerAnchorPosition(
  trigger: HTMLElement,
  picker: HTMLElement | null,
  options: DatePickerAnchorOptions = {},
): DatePickerAnchorRect {
  const rect = trigger.getBoundingClientRect()
  const gap = DATE_PICKER_ANCHOR_GAP
  const width =
    options.pickerWidth ?? Math.max(rect.width, options.minWidth ?? 280)
  const height = picker?.offsetHeight ?? estimateDatePickerHeight(options)

  const spaceBelow = window.innerHeight - rect.bottom - gap
  const spaceAbove = rect.top - gap
  const placeAbove = height > spaceBelow && spaceAbove > spaceBelow

  const top = placeAbove
    ? Math.max(gap, rect.top - height - gap)
    : rect.bottom + gap

  const viewportWidth = window.innerWidth
  const resolveLeft = options.resolveLeft ?? defaultResolveLeft
  const left = resolveLeft(rect, width, viewportWidth)

  return {
    top,
    left,
    width,
    placement: placeAbove ? 'above' : 'below',
  }
}

export function applyDatePickerAnchorStyles(
  picker: HTMLElement,
  position: Pick<DatePickerAnchorRect, 'top' | 'left' | 'width'>,
): void {
  picker.style.top = `${position.top}px`
  picker.style.left = `${position.left}px`
}

export function getOverflowScrollTargets(element: HTMLElement | null): Array<HTMLElement | Window> {
  const targets: Array<HTMLElement | Window> = []
  const seen = new Set<EventTarget>()

  let node: HTMLElement | null = element
  while (node) {
    const style = getComputedStyle(node)
    if (
      /(auto|scroll|overlay)/.test(style.overflowY) ||
      /(auto|scroll|overlay)/.test(style.overflowX)
    ) {
      if (!seen.has(node)) {
        targets.push(node)
        seen.add(node)
      }
    }
    node = node.parentElement
  }

  if (!seen.has(window)) {
    targets.push(window)
  }

  return targets
}

type BindAnchoredOverlayPositionArgs = {
  getTrigger: () => HTMLElement | null
  getPicker: () => HTMLElement | null
  onPosition: (position: DatePickerAnchorRect) => void
  options?: DatePickerAnchorOptions
  extraScrollTargets?: Array<HTMLElement | null | undefined>
}

/** rAF-throttled anchor updates on scroll/resize; applies styles directly when picker is mounted. */
export function bindAnchoredOverlayPosition({
  getTrigger,
  getPicker,
  onPosition,
  options = {},
  extraScrollTargets = [],
}: BindAnchoredOverlayPositionArgs): () => void {
  let rafId: number | null = null

  const flush = () => {
    rafId = null
    const trigger = getTrigger()
    if (!trigger) return

    const picker = getPicker()
    const position = computeDatePickerAnchorPosition(trigger, picker, options)
    if (picker) {
      applyDatePickerAnchorStyles(picker, position)
    }
    onPosition(position)
  }

  const schedule = () => {
    if (rafId != null) return
    rafId = requestAnimationFrame(flush)
  }

  schedule()
  requestAnimationFrame(schedule)

  const trigger = getTrigger()
  const scrollTargets = [
    ...getOverflowScrollTargets(trigger),
    ...extraScrollTargets.filter((el): el is HTMLElement => Boolean(el)),
  ]

  const uniqueTargets = Array.from(new Set(scrollTargets))
  for (const target of uniqueTargets) {
    target.addEventListener('scroll', schedule, { passive: true, capture: true })
  }
  window.addEventListener('resize', schedule, { passive: true })

  return () => {
    if (rafId != null) cancelAnimationFrame(rafId)
    for (const target of uniqueTargets) {
      target.removeEventListener('scroll', schedule, true)
    }
    window.removeEventListener('resize', schedule)
  }
}
